import { useRef, useState, useMemo, useCallback, type ChangeEvent } from 'react'
import {
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Divider,
  Heading,
  HStack,
  Input,
  InputGroup,
  InputRightElement,
  Text,
  VStack,
  Badge,
  useDisclosure,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  FormHelperText,
  IconButton,
} from '@chakra-ui/react'
import { Search } from 'lucide-react'
import { AdminLayout } from '@/components/layout/AdminLayout'
import { DataTable, type DataTableRef, type DataTableOptions } from '@/components/tables'
import { ActionButtons } from '@/components/common/ActionButtons'
import { useThemeColors } from '@/hooks/useThemeColors'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { useAccessCheck } from '@/hooks/useAccess'
import {
  getGraphMailboxSubscriptionStatus,
  createGraphMailboxSubscription,
  renewGraphMailboxSubscription,
  deleteGraphMailboxSubscription,
  type GraphSubscriptionRow,
} from '@/lib/services/graphMailboxSubscriptionService'

function formatExpiry(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleString(undefined, { timeZone: 'UTC' }) + ' UTC'
}

function truncateId(s: string, max = 18): string {
  if (s.length <= max) return s
  return `${s.slice(0, max)}…`
}

function truncateUrl(s: string | null | undefined, max = 40): string {
  if (!s) return '—'
  if (s.length <= max) return s
  return `${s.slice(0, max)}…`
}

/** Graph subscriptions use `changeType` at create time only; inbox “new mail” uses `created`. */
const DEFAULT_CHANGE_TYPE = 'created'

export default function GraphMailboxSubscriptionPage() {
  const colors = useThemeColors()
  const tableRef = useRef<DataTableRef>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const cancelRef = useRef<HTMLButtonElement>(null)
  const { showError, showSuccess } = useToastNotification()
  const access = useAccessCheck()

  const [searchQuery, setSearchQuery] = useState('')
  const [globalBusy, setGlobalBusy] = useState<string | null>(null)
  const [rowBusy, setRowBusy] = useState<Map<number, string>>(() => new Map())
  const [mailboxEmail, setMailboxEmail] = useState('')
  const [notificationUrl, setNotificationUrl] = useState('')
  const [pendingDelete, setPendingDelete] = useState<GraphSubscriptionRow | null>(null)
  const [editingRow, setEditingRow] = useState<GraphSubscriptionRow | null>(null)

  const {
    isOpen: isCreateOpen,
    onOpen: onCreateOpen,
    onClose: onCreateClose,
  } = useDisclosure()
  const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure()
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure()

  const setRowAction = useCallback((id: number, action: string | null) => {
    setRowBusy((prev) => {
      const next = new Map(prev)
      if (action === null) {
        next.delete(id)
      } else {
        next.set(id, action)
      }
      return next
    })
  }, [])

  const anyBusy = globalBusy !== null || rowBusy.size > 0

  const handleRenewRow = useCallback(
    async (row: GraphSubscriptionRow) => {
      setRowAction(row.id, 'renew')
      try {
        const res = await renewGraphMailboxSubscription({ id: row.id })
        if (!res.success) {
          showError(res.message || 'Request failed')
          return
        }
        showSuccess(res.message || 'Renewed')
        tableRef.current?.reload()
      } catch (e) {
        showError(e)
      } finally {
        setRowAction(row.id, null)
      }
    },
    [setRowAction, showError, showSuccess],
  )

  const openDeleteForRow = useCallback(
    (row: GraphSubscriptionRow) => {
      setPendingDelete(row)
      onDeleteOpen()
    },
    [onDeleteOpen],
  )

  const tableOptions: DataTableOptions = useMemo(
    () => ({
      dataFields: {
        mailbox_email: {
          name: 'Mailbox',
          withSort: true,
          maxWidth: '220px',
        },
        subscription_id: {
          name: 'Subscription ID',
          withSort: true,
          maxWidth: '200px',
        },
        notification_url: {
          name: 'Webhook URL',
          withSort: false,
          maxWidth: '240px',
        },
        resource: {
          name: 'Resource',
          withSort: false,
          maxWidth: '280px',
        },
        change_type: {
          name: 'Change type',
          withSort: true,
          maxWidth: '110px',
        },
        is_active: {
          name: 'Active',
          withSort: true,
          maxWidth: '100px',
          badge: {
            true: 'green',
            false: 'gray',
          },
          badgeLabels: {
            true: 'Yes',
            false: 'No',
          },
        },
        expiration_date_time: {
          name: 'Expires (UTC)',
          withSort: true,
          maxWidth: '180px',
        },
      },
      dataSource: '/system-settings/mailbox-graph-subscription/status',
      primaryKey: 'id',
      softDelete: false,
      displayInModal: true,
      noRecordText: 'No Graph mailbox subscriptions yet',
      otherActions: [
        {
          name: 'Renew',
          onClick: (row: GraphSubscriptionRow) => {
            void handleRenewRow(row)
          },
        },
        {
          name: 'Delete',
          onClick: (row: GraphSubscriptionRow) => {
            openDeleteForRow(row)
          },
        },
      ],
    }),
    [handleRenewRow, openDeleteForRow],
  )

  const permissions = useMemo(
    () => ({
      can_edit: access.can_edit,
      can_delete: false,
      can_create: access.can_create,
      can_view: access.can_view,
      can_export: access.can_export,
      can_import: access.can_import,
    }),
    [access],
  )

  const tableParams = useMemo(() => ({ search: searchQuery }), [searchQuery])

  const customRenderers = useMemo(
    () => ({
      subscription_id: (value: string) => (
        <Text as="span" fontFamily="mono" fontSize="xs">
          {truncateId(value || '')}
        </Text>
      ),
      notification_url: (value: string | null) => (
        <Text as="span" fontSize="xs" title={value || undefined}>
          {truncateUrl(value)}
        </Text>
      ),
      expiration_date_time: (value: string | null) => <span>{formatExpiry(value)}</span>,
    }),
    [],
  )

  const handleSearch = () => {
    const q = searchRef.current?.value || ''
    setSearchQuery(q)
    tableRef.current?.applyFilter('search', q)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch()
    }
  }

  const handleCreateClose = () => {
    setMailboxEmail('')
    setNotificationUrl('')
    onCreateClose()
  }

  const handleEditClose = () => {
    setEditingRow(null)
    onEditClose()
  }

  const openEditFromTable = useCallback(
    (row: unknown) => {
      setEditingRow(row as GraphSubscriptionRow)
      onEditOpen()
    },
    [onEditOpen],
  )

  const runGlobal = async (key: string, fn: () => Promise<{ success: boolean; message?: string }>) => {
    setGlobalBusy(key)
    try {
      const res = await fn()
      if (!res.success) {
        showError(res.message || 'Request failed')
        return
      }
      showSuccess(res.message || 'Done')
      tableRef.current?.reload()
    } catch (e) {
      showError(e)
    } finally {
      setGlobalBusy(null)
    }
  }

  const handleCreateSubmit = async () => {
    const email = mailboxEmail.trim()
    if (!email) {
      showError('Mailbox email is required')
      return
    }
    setGlobalBusy('create')
    try {
      const nu = notificationUrl.trim()
      const res = await createGraphMailboxSubscription({
        mailbox_email: email,
        change_type: DEFAULT_CHANGE_TYPE,
        ...(nu ? { notification_url: nu } : {}),
      })
      if (!res.success) {
        showError(res.message || 'Request failed')
        return
      }
      showSuccess(res.message || 'Subscription created')
      handleCreateClose()
      tableRef.current?.reload()
    } catch (e) {
      showError(e)
    } finally {
      setGlobalBusy(null)
    }
  }

  const runRenewInEdit = async () => {
    if (!editingRow) return
    const row = editingRow
    setRowAction(row.id, 'renew')
    try {
      const res = await renewGraphMailboxSubscription({ id: row.id })
      if (!res.success) {
        showError(res.message || 'Request failed')
        return
      }
      showSuccess(res.message || 'Renewed')
      tableRef.current?.reload()
      const updated = (await getGraphMailboxSubscriptionStatus()).data?.find((r) => r.id === row.id)
      if (updated) {
        setEditingRow(updated)
      }
    } catch (e) {
      showError(e)
    } finally {
      setRowAction(row.id, null)
    }
  }

  const confirmDelete = async () => {
    if (!pendingDelete) return
    const row = pendingDelete
    setRowAction(row.id, 'delete')
    try {
      const res = await deleteGraphMailboxSubscription({ id: row.id })
      if (!res.success) {
        showError(res.message || 'Request failed')
        return
      }
      showSuccess(res.message || 'Deleted')
      if (editingRow?.id === row.id) {
        handleEditClose()
      }
      tableRef.current?.reload()
    } catch (e) {
      showError(e)
    } finally {
      setRowAction(row.id, null)
      setPendingDelete(null)
      onDeleteClose()
    }
  }

  return (
    <AdminLayout>
      <Box p={4} h="100%" overflow="hidden" display="flex" flexDirection="column">
        <VStack spacing={2} align="stretch" flex="1" overflow="hidden" minH={0}>
          <Card flexShrink={0} display="flex" flexDirection="column" flex="1" minH={0} overflow="hidden">
            <CardHeader flexShrink={0} py={3}>
              <Heading size="md" mb={1}>
                Microsoft Graph mailbox subscriptions
              </Heading>
              <Text fontSize="sm" color={colors.text}>
                Same actions as{' '}
                <Text as="span" fontFamily="mono" fontSize="xs">
                  php artisan graph:subscription
                </Text>
                . Requires{' '}
                <Text as="span" fontWeight="semibold">
                  MICROSOFT_WEBHOOK_URL
                </Text>{' '}
                and{' '}
                <Text as="span" fontWeight="semibold">
                  MICROSOFT_WEBHOOK_CLIENT_STATE
                </Text>
                . New subscriptions use Graph change type <strong>created</strong> (new messages in Inbox)—that is
                separate from deleting a subscription.
              </Text>
            </CardHeader>

            <CardHeader py={0} px={0} flexShrink={0}>
              <Box pt={2} pb={0}>
                <HStack justify="space-between" align="flex-end" spacing={4} px={4} width="100%">
                  <Box pb={2}>
                    <Text fontSize="sm" fontWeight="medium" color={colors.heading}>
                      Subscriptions
                    </Text>
                  </Box>
                  <Box pb={2}>
                    <HStack spacing={2}>
                      <Button
                        size="sm"
                        variant="outline"
                        isLoading={globalBusy === 'renew-all'}
                        isDisabled={anyBusy}
                        onClick={() =>
                          void runGlobal('renew-all', () => renewGraphMailboxSubscription())
                        }
                      >
                        Renew all (near expiry)
                      </Button>
                      <ActionButtons
                        pathname="/system-settings/mailbox-graph-subscription"
                        createLabel="Add subscription"
                        onCreate={() => {
                          setMailboxEmail('')
                          setNotificationUrl('')
                          onCreateOpen()
                        }}
                      />
                    </HStack>
                  </Box>
                </HStack>
                <Box borderBottom="2px" borderColor={colors.border} mx={4} />
              </Box>
            </CardHeader>

            <CardHeader py={1} pt={3} pb={2} flexShrink={0}>
              <HStack spacing={4} flexWrap="wrap">
                <Box flex="0 1 auto" minW="250px" maxW="400px">
                  <InputGroup size="sm">
                    <Input
                      ref={searchRef}
                      placeholder="Search mailbox, subscription id…"
                      onKeyDown={handleKeyDown}
                      defaultValue={searchQuery}
                      pr="2.5rem"
                    />
                    <InputRightElement width="2.5rem">
                      <IconButton
                        aria-label="Search"
                        icon={<Search size={16} />}
                        size="sm"
                        h="1.75rem"
                        w="1.75rem"
                        onClick={handleSearch}
                        variant="ghost"
                      />
                    </InputRightElement>
                  </InputGroup>
                </Box>
              </HStack>
            </CardHeader>

            <CardBody
              p={0}
              pt={1}
              pb={4}
              mx={5}
              flex="1"
              overflow="hidden"
              display="flex"
              flexDirection="column"
              minH={0}
              position="relative"
            >
              <Box h="100%" display="flex" flexDirection="column" minH={0}>
                <DataTable
                  ref={tableRef}
                  options={tableOptions}
                  params={tableParams}
                  access={permissions}
                  customRenderers={customRenderers}
                  onOpenModal={openEditFromTable}
                />
              </Box>
            </CardBody>
          </Card>
        </VStack>
      </Box>

      <Modal isOpen={isCreateOpen} onClose={handleCreateClose} size="xl" closeOnOverlayClick={!anyBusy}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Add subscription</ModalHeader>
          <ModalCloseButton isDisabled={globalBusy === 'create'} />
          <ModalBody>
            <VStack align="stretch" spacing={4}>
              <FormControl isRequired>
                <FormLabel>Mailbox (UPN / email)</FormLabel>
                <Input
                  placeholder="helpdesk@contoso.com"
                  value={mailboxEmail}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setMailboxEmail(e.target.value)}
                  isDisabled={globalBusy === 'create'}
                />
                <FormHelperText>
                  Subscribes to <strong>new inbox messages</strong> using Microsoft Graph <code>changeType</code>{' '}
                  <strong>created</strong> (not related to the Delete action on a row).
                </FormHelperText>
              </FormControl>
              <FormControl>
                <FormLabel>Notification / webhook URL (optional)</FormLabel>
                <Input
                  placeholder="Leave blank to use server default (MICROSOFT_WEBHOOK_URL)"
                  value={notificationUrl}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setNotificationUrl(e.target.value)}
                  isDisabled={globalBusy === 'create'}
                />
                <FormHelperText>
                  Per Microsoft Graph, each subscription is tied to one public <strong>https</strong> URL. Host must
                  match <code>MICROSOFT_WEBHOOK_URL</code> or be listed in{' '}
                  <code>MICROSOFT_WEBHOOK_URL_ALLOWED_HOSTS</code>. To change the URL later, delete the subscription and
                  create a new one.
                </FormHelperText>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={handleCreateClose} isDisabled={globalBusy === 'create'}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              isLoading={globalBusy === 'create'}
              onClick={() => void handleCreateSubmit()}
            >
              Create
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={isEditOpen} onClose={handleEditClose} size="lg" closeOnOverlayClick={!anyBusy}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Edit subscription</ModalHeader>
          <ModalCloseButton isDisabled={editingRow != null && rowBusy.get(editingRow.id) != null} />
          <ModalBody>
            {editingRow && (
              <VStack align="stretch" spacing={4}>
                <Text fontSize="sm" color={colors.textMuted}>
                  Mailbox cannot be changed on an existing Graph subscription. Delete and add a new subscription to use
                  a different mailbox.
                </Text>
                <Divider />
                <FormControl>
                  <FormLabel>Local ID</FormLabel>
                  <Input value={String(editingRow.id)} isReadOnly />
                </FormControl>
                <FormControl>
                  <FormLabel>Subscription ID (Graph)</FormLabel>
                  <Input value={editingRow.subscription_id} isReadOnly fontFamily="mono" fontSize="sm" />
                </FormControl>
                <FormControl>
                  <FormLabel>Mailbox</FormLabel>
                  <Input value={editingRow.mailbox_email || '—'} isReadOnly />
                </FormControl>
                <FormControl>
                  <FormLabel>Change type (stored at create)</FormLabel>
                  <Input value={editingRow.change_type || DEFAULT_CHANGE_TYPE} isReadOnly />
                </FormControl>
                <FormControl>
                  <FormLabel>Webhook URL (stored at create)</FormLabel>
                  <Input value={editingRow.notification_url || '—'} isReadOnly fontSize="sm" />
                </FormControl>
                <FormControl>
                  <FormLabel>Resource</FormLabel>
                  <Input value={editingRow.resource || '—'} isReadOnly fontSize="sm" />
                </FormControl>
                <FormControl>
                  <FormLabel>Expires (UTC)</FormLabel>
                  <Input value={formatExpiry(editingRow.expiration_date_time)} isReadOnly />
                </FormControl>
                <FormControl>
                  <FormLabel>Active</FormLabel>
                  <HStack>
                    <Badge colorScheme={editingRow.is_active ? 'green' : 'gray'}>
                      {editingRow.is_active ? 'Yes' : 'No'}
                    </Badge>
                  </HStack>
                </FormControl>
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr="auto" onClick={handleEditClose}>
              Close
            </Button>
            <HStack spacing={2}>
              <Button
                colorScheme="red"
                variant="outline"
                isDisabled={!editingRow}
                isLoading={editingRow != null && rowBusy.get(editingRow.id) === 'delete'}
                onClick={() => {
                  if (editingRow) {
                    openDeleteForRow(editingRow)
                  }
                }}
              >
                Delete
              </Button>
              <Button
                colorScheme="blue"
                isDisabled={!editingRow}
                isLoading={editingRow != null && rowBusy.get(editingRow.id) === 'renew'}
                onClick={() => void runRenewInEdit()}
              >
                Renew
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <AlertDialog isOpen={isDeleteOpen} leastDestructiveRef={cancelRef} onClose={onDeleteClose}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete Graph subscription?
            </AlertDialogHeader>
            <AlertDialogBody>
              This removes the subscription on Microsoft Graph for{' '}
              <Text as="span" fontWeight="semibold">
                {pendingDelete?.mailbox_email || 'this mailbox'}
              </Text>{' '}
              and marks the row inactive locally.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button
                ref={cancelRef}
                onClick={() => {
                  setPendingDelete(null)
                  onDeleteClose()
                }}
              >
                Cancel
              </Button>
              <Button
                colorScheme="red"
                ml={3}
                isLoading={pendingDelete != null && rowBusy.get(pendingDelete.id) === 'delete'}
                onClick={() => void confirmDelete()}
              >
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </AdminLayout>
  )
}
